import { useEffect, useState } from "react";
import { postGfNote, updateGfDeal } from "../../../../api/giftingClient";
import { fmtDateTime } from "../../../../lib/giftingRules";
import type { GfDealDetail } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { GfButton, GfField } from "../../ui/GfUi";

const LOG_LIMIT = 5;

/** メモ（エントリーのメモ欄）と活動ログ（最近の記録＋ひと言追記） */
export function NotesSection({
  detail,
  onChanged,
  toast,
}: {
  detail: GfDealDetail;
  onChanged: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const [notes, setNotes] = useState(detail.notes ?? "");
  const [line, setLine] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setNotes(detail.notes ?? ""), [detail.notes]);

  const saveNotes = async () => {
    setBusy(true);
    try {
      await updateGfDeal(detail.id, { notes: notes.trim() || null });
      toast("メモを保存しました");
      await onChanged();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const addLine = async () => {
    if (!line.trim()) return;
    setBusy(true);
    try {
      await postGfNote({ body: line.trim(), deal_id: detail.id, influencer_id: detail.influencer_id });
      setLine("");
      toast("活動ログに追記しました");
      await onChanged();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const log = [...detail.activity].sort((a, b) => (b.created_at > a.created_at ? 1 : -1)).slice(0, LOG_LIMIT);

  return (
    <section className="gf-panel-section">
      <div className="gf-panel-section-title">メモ</div>
      <GfField label="メモ（このエントリー）">
        <textarea className="gf-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </GfField>
      <div className="gf-actions">
        <GfButton size="sm" disabled={busy || (notes.trim() || null) === (detail.notes?.trim() || null)} onClick={() => void saveNotes()}>
          メモを保存
        </GfButton>
      </div>
      <div className="gf-panel-section-title gf-log-title">活動ログ</div>
      {log.length === 0 && <div className="gf-note">記録はまだありません。</div>}
      {log.map((a) => (
        <div className="gf-log-row" key={a.id}>
          <span className="gf-mono gf-log-date">{fmtDateTime(a.created_at)}</span>
          <span className="gf-log-body">{a.body}</span>
        </div>
      ))}
      <div className="gf-log-add">
        <input
          className="gf-input"
          value={line}
          onChange={(e) => setLine(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void addLine()}
          placeholder="ひと言を記録（例: DMで返信あり）"
        />
        <GfButton size="sm" disabled={busy || !line.trim()} onClick={() => void addLine()}>
          追記
        </GfButton>
      </div>
    </section>
  );
}
